import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, ArrowUp, ArrowDown, Book } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

/**
 * Editor della brochure homepage:
 * - Titolo e sottotitolo della sezione
 * - URL del PDF completo (opzionale)
 * - Lista delle pagine immagine (upload da PC) con riordino e eliminazione
 */
export default function BrochureEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getBrochure().then((d) => setData({
      title: d.title || 'Brochure Ufficiale',
      subtitle: d.subtitle || '',
      pdf_url: d.pdf_url || '',
      pages: d.pages || [],
    })).catch(() => setData({ title: 'Brochure Ufficiale', subtitle: '', pdf_url: '', pages: [] }));
  }, []);

  if (!data) return <Loader />;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const addPage = () => set('pages', [...data.pages, { src: '', label: `Pagina ${data.pages.length + 1}` }]);
  const addManyPages = (urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return;
    const start = data.pages.length;
    const next = urls.map((src, i) => ({ src, label: `Pagina ${start + i + 1}` }));
    set('pages', [...data.pages, ...next]);
  };
  const updatePage = (i, k, v) => {
    const next = [...data.pages]; next[i] = { ...next[i], [k]: v };
    set('pages', next);
  };
  const removePage = (i) => set('pages', data.pages.filter((_, idx) => idx !== i));
  const movePage = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= data.pages.length) return;
    const next = [...data.pages]; [next[i], next[j]] = [next[j], next[i]];
    set('pages', next);
  };

  const save = async () => {
    setSaving(true);
    try { await api.saveBrochure(data); toast.success('Brochure aggiornata'); }
    catch (e) { toast.error(e?.message || 'Errore salvataggio'); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="brochure-editor">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-white font-bold text-lg flex items-center gap-2"><Book size={18} className="text-pink-brand" /> Brochure (homepage)</h2>
          <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-5" data-testid="brochure-save-button"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Titolo sezione</label><input className={inp} value={data.title} onChange={(e) => set('title', e.target.value)} /></div>
          <div><label className={lbl}>Sottotitolo sezione</label><input className={inp} value={data.subtitle} onChange={(e) => set('subtitle', e.target.value)} /></div>
        </div>
        <div className="mt-3">
          <UploadField label="PDF completo della brochure (opzionale)" accept="application/pdf" folder="brochure" value={data.pdf_url} onChange={(v) => set('pdf_url', v)} />
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">Pagine della brochure ({data.pages.length})</h3>
          <button onClick={addPage} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Aggiungi pagina vuota</button>
        </div>
        <div className="bg-navy-dark/40 border border-white/10 rounded-lg p-3 mb-4">
          <UploadField label="Carica più pagine in un colpo solo (selezione multipla)" accept="image/*" folder="brochure" multiple onMultiUpload={addManyPages} />
        </div>
        <p className="text-white/60 text-xs mb-4">Trascina più immagini insieme nell'area sopra per aggiungerle tutte. Riordina con le frecce, elimina con il cestino.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {data.pages.map((p, i) => (
            <div key={i} className="bg-navy-dark border border-white/10 rounded-xl p-3" data-testid={`brochure-page-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-pink-brand font-bold text-sm">#{i + 1}</span>
                <input className={inp + ' flex-1'} value={p.label || ''} onChange={(e) => updatePage(i, 'label', e.target.value)} placeholder={`Pagina ${i + 1}`} />
                <div className="flex">
                  <button onClick={() => movePage(i, -1)} disabled={i === 0} className="p-1.5 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowUp size={14} /></button>
                  <button onClick={() => movePage(i, 1)} disabled={i === data.pages.length - 1} className="p-1.5 text-white/60 hover:text-pink-brand disabled:opacity-30"><ArrowDown size={14} /></button>
                  <button onClick={() => removePage(i)} className="p-1.5 text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <UploadField accept="image/*" folder="brochure" value={p.src} onChange={(v) => updatePage(i, 'src', v)} />
            </div>
          ))}
          {data.pages.length === 0 && (
            <div className="sm:col-span-2 text-center text-white/50 text-sm py-8">Nessuna pagina. Clicca "Aggiungi pagina" per iniziare.</div>
          )}
        </div>
      </Card>

      <button onClick={save} disabled={saving} className="btn-primary"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
    </div>
  );
}
